import RtkQueryService from '@lok/services/RtkQueryService';

import { inlineErrorResponse } from './api';

// CON-306 learner features: search, the Progress tab (course + goal
// progress) and the course assistant. Every endpoint is the signed-in
// user's own data. All use the non-redirecting error transform: none of
// these failures should send the app to /404.
const learnerService = RtkQueryService.injectEndpoints({
  endpoints: (builder) => ({
    learnerSearch: builder.query({
      query: ({ q, exact }) => ({
        url: '/v1/learner/search/',
        params: exact ? { q, exact: 1 } : { q }
      }),
      keepUnusedDataFor: 30,
      transformErrorResponse: inlineErrorResponse
    }),
    learnerCourseProgress: builder.query({
      query: () => ({ url: '/v1/learner/progress/courses/' }),
      providesTags: ['LearnerCourseProgress'],
      transformErrorResponse: inlineErrorResponse
    }),
    learnerGoal: builder.query({
      query: () => ({ url: '/v1/learner/progress/goal/' }),
      providesTags: ['LearnerGoal'],
      transformErrorResponse: inlineErrorResponse
    }),
    setLearnerGoal: builder.mutation({
      // A goal name, or { goal, id } for one of the listed goals.
      query: (goal) => ({
        url: '/v1/learner/progress/goal/',
        method: 'PATCH',
        data: typeof goal === 'object' ? goal : { goal }
      }),
      invalidatesTags: ['LearnerGoal'],
      transformErrorResponse: inlineErrorResponse
    }),
    updateGoalTopic: builder.mutation({
      query: ({ topic, data }) => ({
        url: `/v1/learner/progress/goal/topics/${topic}/`,
        method: 'PATCH',
        data
      }),
      invalidatesTags: ['LearnerGoal'],
      transformErrorResponse: inlineErrorResponse
    }),
    uploadGoalFile: builder.mutation({
      // outcomeId: the tangible outcome this file is for (Dane, CON-306).
      query: ({ topic, file, outcomeId }) => {
        const data = new FormData();
        data.append('file', file);
        data.append('outcome_id', outcomeId);
        return {
          url: `/v1/learner/progress/goal/topics/${topic}/uploads/`,
          method: 'POST',
          data
        };
      },
      invalidatesTags: ['LearnerGoal'],
      transformErrorResponse: inlineErrorResponse
    }),
    deleteGoalUpload: builder.mutation({
      query: (id) => ({ url: `/v1/learner/progress/goal/uploads/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['LearnerGoal'],
      transformErrorResponse: inlineErrorResponse
    }),
    // Super admin: the goals learners pick, their sections and deliverables
    // (the Goals card). Saving refetches the learner's own Goal Progress too.
    goalsAdmin: builder.query({
      query: () => ({ url: '/v1/learner/goals/' }),
      providesTags: ['GoalsAdmin'],
      transformErrorResponse: inlineErrorResponse
    }),
    saveGoalAdmin: builder.mutation({
      query: ({ id, data }) =>
        id
          ? { url: `/v1/learner/goals/${id}/`, method: 'PUT', data }
          : { url: '/v1/learner/goals/', method: 'POST', data },
      invalidatesTags: ['GoalsAdmin', 'LearnerGoal'],
      transformErrorResponse: inlineErrorResponse
    }),
    deleteGoalAdmin: builder.mutation({
      query: (id) => ({ url: `/v1/learner/goals/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['GoalsAdmin', 'LearnerGoal'],
      transformErrorResponse: inlineErrorResponse
    }),
    assistantStatus: builder.query({
      query: () => ({ url: '/v1/learner/assistant/' }),
      providesTags: ['AssistantStatus'],
      transformErrorResponse: inlineErrorResponse
    }),
    askAssistant: builder.mutation({
      // includeFiles: the learner's explicit choice to share their recent
      // uploads with this one question.
      query: ({ messages, includeFiles = false }) => ({
        url: '/v1/learner/assistant/',
        method: 'POST',
        data: { messages, include_files: includeFiles }
      }),
      transformErrorResponse: inlineErrorResponse
    })
  })
});

export const {
  useLearnerSearchQuery,
  useLearnerCourseProgressQuery,
  useLearnerGoalQuery,
  useSetLearnerGoalMutation,
  useUpdateGoalTopicMutation,
  useUploadGoalFileMutation,
  useDeleteGoalUploadMutation,
  useGoalsAdminQuery,
  useSaveGoalAdminMutation,
  useDeleteGoalAdminMutation,
  useAssistantStatusQuery,
  useAskAssistantMutation
} = learnerService;
